import { EventEmitter, Injectable, inject } from '@angular/core';
import { IApiRequest } from '@core/interfaces/api-request';
import { ApiService } from '@core/services/api.service';
import { AuthService } from '@core/services/auth.service';
import { ModalUtilsService } from '@core/services/modal-utils.service';
import { ApiServiceHolder } from '@core/utils/api-service-holder';
import { NoPaymentMethodDialogComponent } from '@shared/components/no-payment-method-dialog/no-payment-method-dialog.component';
import { Observable, filter, map } from 'rxjs';
import { BidReservePriceComponent } from 'src/app/article/components/dialogs/bid-reserve-price/bid-reserve-price.component';
import { DirectOfferDialogComponent } from 'src/app/article/components/dialogs/direct-offer-dialog/direct-offer-dialog.component';
import { DirectOfferMadeDialogComponent } from 'src/app/article/components/dialogs/direct-offer-made-dialog/direct-offer-made-dialog/direct-offer-made-dialog.component';
import { EnumBidResult } from 'src/app/article/enums/bid-result.enum';
import { DialogArticleType } from 'src/app/article/interfaces/article-bid.interface';
import { IBidResponse } from 'src/app/article/interfaces/bid-response.interface';
import { AuctionReserveDto } from 'src/app/articles-manager/interfaces/auction.interface';
import { AuctionViewService } from 'src/app/articles-manager/services/auction-view.service';
import { AccountDialogComponent } from 'src/app/login/components/account-dialog/account-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class BidService {
  private authService = inject(AuthService);
  private _api = inject(ApiService);
  private modalUtils = inject(ModalUtilsService);
  private readonly auctionViewService = inject(AuctionViewService);

  makeOfferMessage(idAuction: number) {
    if (this.authService.isAuthenticated()) {
      this._api
        .request<boolean>(ApiServiceHolder.message.checkCanOffer(idAuction))
        .subscribe((dataR) => {
          const canMakeDirectOffer = dataR;
          if (canMakeDirectOffer) {
            this.auctionViewService.getByPk(idAuction).subscribe((auction) => {
              const ref = this.modalUtils.openModal(
                DirectOfferDialogComponent,
                {
                  size: 'lg',
                  keyboard: false,
                }
              );
              ref.componentInstance.isMemorabid = auction.idAuctionType === 1;
              ref.componentInstance.idAuction = auction.idAuction;
              ref.componentInstance.idUserWinner = auction.idUserWinner;
              ref.componentInstance.winnerUsername = auction.winnerUsername;
              ref.componentInstance.entitySellerName = auction.entitySellerName;
              ref.componentInstance.article.set(auction.article);
              ref.componentInstance.sportIds =
                auction.article.sportTranslationKey;
              ref.componentInstance.competitionIds =
                auction.article.competitionTranslationKey;
              ref.componentInstance.teamIds =
                auction.article.teamTranslationKey;

              ref.result.then(() => {});
            });
          } else {
            this.modalUtils.openModal(DirectOfferMadeDialogComponent, {
              size: 'md',
              keyboard: false,
            });
          }
        });
    } else {
      this.modalUtils.openModal(AccountDialogComponent, {
        size: 'md',
        keyboard: false,
      });
    }
  }

  makeBidReservePrice(
    idAuction: number,
    onSuccessBidEvent?: EventEmitter<DialogArticleType>
  ) {
    this.auctionViewService
      .getReserve(idAuction, this.authService.userLogged.idUser)
      .subscribe((dataR) => {
        if (dataR == null) {
          return;
        }

        const reservePrice = dataR;
        const ref = this.modalUtils.openModal(BidReservePriceComponent, {
          size: 'md',
          keyboard: false,
        });
        ref.componentInstance.reservePrice = reservePrice;
        ref.componentInstance.idAuction = idAuction;
        ref.result.then((result) => {
          if (result) {
            const auctionReserveDto: AuctionReserveDto = {
              idAuction: idAuction,
            };
            this.filterNoPaymentApiResponse(
              ApiServiceHolder.auction.reserveBid(auctionReserveDto)
            ).subscribe((bidResponse) => {
              if (bidResponse.bidResult == EnumBidResult.Success) {
                onSuccessBidEvent?.emit('BID');
              } else {
                throw new Error('Server error on bid');
              }
            });
          }
        });
      });
  }

  private filterNoPaymentApiResponse(
    apiRequest: IApiRequest
  ): Observable<IBidResponse> {
    return this._api.request<IBidResponse>(apiRequest).pipe(
      map((result) => {
        if (result.bidResult == EnumBidResult.NoPaymentMethod) {
          let ref = this.modalUtils.openModal(NoPaymentMethodDialogComponent, {
            size: 'lg',
            keyboard: false,
            backdrop: 'static',
          });
          ref.result.then(
            () => {
              location.replace(result.stripeCheckoutSessionUrl);
            },
            () => {
              /* do nothing on close click */
            }
          );
        }

        return result;
      }),
      filter((result) => result.bidResult !== EnumBidResult.NoPaymentMethod)
    );
  }
}
